import "./PlanScreen.css"
import "./PixCheckoutScreen.css"
import "./SubscriptionScreen.css"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useSnackbar } from "react-simple-snackbar"
import { Skeleton } from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import requests from "../api/requests"
import { handleAPIError } from "../api/error"
import { getUser } from "../app/features/userSlice"
import {
  UNPAID_STATUSES,
  fetchSubscriptionStatus,
  selectMigration,
  selectPending,
  selectPlan,
  selectPlanChangeAvailable,
  selectReauthorize,
  selectScheduledPlan,
  selectSubscriptionStatus,
  selectSwitch,
  selectTrial,
  routeForStatus,
} from "../app/features/subscriptionStatusSlice"
import Button from "../components/Button"
import CosmosBackground from "../components/CosmosBackground"
import AddressForm, { EMPTY_ADDRESS, addressFromProfile } from "../components/AddressForm"
import PixQrCode from "../components/PixQrCode"
import {
  migrationNotice,
  openPendingPayment,
  pendingActionLabel,
  pendingNotice,
} from "../components/pendingPayment"
import { DEFAULT_ERROR_MESSAGE, errorSnackBar, successSnackBar } from "../components/snackBarOptions"
import { formatDate, formatDayMonth, formatInstantDate } from "../utils/dates"

const METHOD_LABELS = {
  card: "Cartão de crédito",
  boleto: "Boleto",
  pix_automatic: "Pix Automático",
  pix: "Pix — um código a cada renovação",
}

const PLAN_LABELS = { month: "Mensal", year: "Anual" }

// Codes the self-service endpoints answer with (api spec asaas-pix, Phases 5
// and 9).
const ERROR_MESSAGES = {
  pending_payment: "Pague a cobrança em aberto antes de continuar.",
  plan_change_unavailable: "A mudança de plano não está disponível para a sua assinatura.",
  no_plan_change: "Não há mudança de plano agendada.",
  switch_window_closed: "Essa opção só fica disponível nos dias antes da renovação.",
  reauthorize_unavailable: "O débito automático não está disponível no momento.",
  no_open_charge: "Não há nenhuma cobrança Pix em aberto.",
  pix_unavailable: "O Pix não está disponível para a sua conta.",
  invalid_address: "Confira o endereço: CEP com 8 dígitos e UF com 2 letras.",
  invalid_city: "Escolha a cidade na lista.",
  invalid_price: "Esse plano não está mais disponível. Escolha outro.",
  migration_required: "Escolha Pix ou cartão para continuar a assinatura.",
}

const MIN_CARD_TRIAL_MS = 48 * 60 * 60 * 1000

const formatPrice = (amount) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(amount)

const formatInterval = (interval) => (interval === "year" ? "por ano" : "por mês")

const planValue = (plan) =>
  plan ? `${PLAN_LABELS[plan.interval] ?? ""} · ${formatPrice(plan.amount ?? 0)} ${formatInterval(plan.interval)}` : "—"

const otherInterval = (interval) => (interval === "year" ? "month" : "year")


// Success rules of the QR flows, read from the polled status.
const switchedToPix = (s) => s?.provider === "asaas" && s?.status === "active" && Boolean(s?.has_access)
const chargePaid = (s) => s?.status === "active" && Boolean(s?.has_access) && !s?.pending
const reauthorized = (s) => s?.payment_method === "pix_automatic"

/**
 * /subscription — "Minha assinatura" (spec asaas-pix W3): status, payment
 * method, renewal, and the actions each state allows: cancel / undo, switch
 * card ↔ Pix on the plan the member picks (D50), pay the open Pix charge,
 * reauthorize Pix Automático (D44, D48), boleto migration (D18). A member
 * with a pending payment can only settle it (D49).
 */
export default function SubscriptionScreen() {
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [openErrorSnackbar] = useSnackbar(errorSnackBar)
  const [openSuccessSnackbar] = useSnackbar(successSnackBar)

  const status = useSelector(selectSubscriptionStatus)
  const pending = useSelector(selectPending)
  const migration = useSelector(selectMigration)
  const switchWindow = useSelector(selectSwitch)
  const reauthorize = useSelector(selectReauthorize)
  const plan = useSelector(selectPlan)
  const scheduledPlan = useSelector(selectScheduledPlan)
  const planChangeAvailable = useSelector(selectPlanChangeAvailable)
  const trial = useSelector(selectTrial)
  const fetched = useSelector((s) => s.subscriptionStatus.lastFetchedAt > 0)

  // overview | plans | address | qr
  const [view, setView] = useState({ name: "overview" })
  // Inline confirmation on the overview: null | { kind: "cancel" } |
  // { kind: "plan", target, monthlyAmount }
  const [dialog, setDialog] = useState(null)
  // Each QR view gets its own key: the QR payload is a bearer secret and
  // never a React key.
  const qrSeq = useRef(0)
  // Bumped when the api answers invalid_city: the address form asks for a
  // city from the list (D71).
  const [invalidCity, setInvalidCity] = useState(0)
  const [busy, setBusy] = useState(null)
  const [plans, setPlans] = useState(null)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [initialAddress, setInitialAddress] = useState(null)

  const refresh = useCallback(() => dispatch(fetchSubscriptionStatus()), [dispatch])
  // Redirects wait for the status fetched on mount, never the cached one.
  const [fresh, setFresh] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate("/")
      return
    }
    refresh().finally(() => setFresh(true))
  }, [user, navigate, refresh])

  const rawStatus = status?.status
  useEffect(() => {
    if (!fresh || view.name !== "overview") return
    // past_due / unpaid are settled here; only checkouts and ended ones leave.
    const route = routeForStatus(rawStatus)
    if (route !== "/unpaid") navigate(route)
  }, [fresh, rawStatus, view.name, navigate])

  const periodEnd = formatInstantDate(status?.current_period_end)

  const handleError = (error) => {
    const code = error?.response?.data?.message
    const httpStatus = error?.response?.status
    if (httpStatus === 401) {
      handleAPIError(error, { dispatch, navigate, openErrorSnackbar })
      return
    }
    if (code === "user_blacklisted") {
      navigate("/blocked")
      return
    }
    if (code === "invalid_city") {
      setInvalidCity((n) => n + 1)
      return
    }
    openErrorSnackbar(ERROR_MESSAGES[code] ?? DEFAULT_ERROR_MESSAGE)
    if (httpStatus === 409) refresh()
  }

  const run = async (key, fn) => {
    setBusy(key)
    try {
      await fn()
    } catch (error) {
      handleError(error)
    } finally {
      setBusy(null)
    }
  }

  const openPortal = () =>
    run("portal", async () => {
      const res = await requests.createPortalSession({ returnURL: window.location.href })
      if (res?.data?.url) window.location.href = res.data.url
    })

  const confirmCancel = () =>
    run("cancel", async () => {
      await requests.cancelSubscription()
      await refresh()
      setDialog(null)
      openSuccessSnackbar(
        trial?.state === "running"
          ? `Teste cancelado. Você mantém o acesso até ${formatDate(trial.endsAt)} e nada será cobrado.`
          : `Assinatura cancelada. Você mantém o acesso até ${periodEnd}.`
      )
    })

  const reactivate = () =>
    run("reactivate", async () => {
      await requests.reactivateSubscription()
      await refresh()
      openSuccessSnackbar(
        trial?.state === "running"
          ? `Pronto! Seu teste continua até ${formatDate(trial.endsAt)}.`
          : "Pronto! Sua assinatura continua ativa."
      )
    })

  // The other interval's plan from the catalog, for the confirmation copy.
  const askPlanChange = () =>
    run("plan", async () => {
      const res = await requests.getPlans()
      const list = res.data ?? []
      const target = list.find((p) => p.recurrentInterval === otherInterval(plan?.interval))
      if (!target) throw new Error("no plan for the other interval")
      const monthly = list.find((p) => p.recurrentInterval === "month")
      setDialog({ kind: "plan", target, monthlyAmount: monthly?.amount ?? null })
    })

  const confirmPlanChange = () =>
    run("plan", async () => {
      await requests.changePlan({ price_id: dialog.target.priceID })
      await refresh()
      setDialog(null)
      openSuccessSnackbar(
        `Pronto! Seu plano muda para o ${PLAN_LABELS[dialog.target.recurrentInterval]} em ${periodEnd}.`
      )
    })

  const undoPlanChange = () =>
    run("undoPlan", async () => {
      await requests.cancelPlanChange()
      await refresh()
      openSuccessSnackbar(`Pronto! Você continua no plano ${PLAN_LABELS[plan?.interval] ?? "atual"}.`)
    })

  // The current plan comes preselected: switching the method alone keeps it
  // (D74); the member may still pick the other one (D50).
  const startSwitch = (to) =>
    run(`switch-${to}`, async () => {
      const res = await requests.getPlans()
      const list = res.data ?? []
      setPlans(list)
      setSelectedPlan(list.find((p) => p.recurrentInterval === plan?.interval) ?? list[0] ?? null)
      setView({ name: "plans", to })
    })

  const switchToCard = () =>
    run("plans", async () => {
      const back = `${window.location.origin}/subscription`
      const res = await requests.switchSubscription({
        to: "card",
        price_id: selectedPlan.priceID,
        success_url: back,
        cancel_url: back,
      })
      if (res?.data?.url) window.location.href = res.data.url
    })

  const goToAddress = () =>
    run("plans", async () => {
      try {
        const res = await requests.getBillingProfile()
        setInitialAddress(addressFromProfile(res.data))
      } catch {
        setInitialAddress({ ...EMPTY_ADDRESS })
      }
      setView({ name: "address", priceID: selectedPlan.priceID })
    })

  const switchToPix = (priceID, address) =>
    run("address", async () => {
      const res = await requests.switchSubscription({ to: "pix", price_id: priceID, address })
      setView({
        name: "qr",
        key: ++qrSeq.current,
        qr: res.data.qr,
        paymentMethod: res.data.payment_method,
        isPaid: switchedToPix,
        paidMessage: `Pronto! Sua assinatura passa para o Pix em ${periodEnd}.`,
        regenerate: () => switchToPix(priceID, address),
      })
    })

  const pay = (reauthorizeFlag) =>
    run(reauthorizeFlag ? "reauthorize" : "pay", async () => {
      const res = await requests.payPendingSubscription(reauthorizeFlag ? { reauthorize: true } : {})
      setView({
        name: "qr",
        key: ++qrSeq.current,
        qr: res.data.qr,
        paymentMethod: reauthorizeFlag ? "pix_automatic" : null,
        isPaid: reauthorizeFlag ? reauthorized : chargePaid,
        paidMessage: reauthorizeFlag
          ? "Débito automático ativado. As próximas renovações serão debitadas automaticamente."
          : "Pagamento confirmado. Sua assinatura segue ativa.",
        regenerate: () => pay(reauthorizeFlag),
      })
    })

  // "Pagar agora" on the Pix trial success view opens the QR here (T10).
  const payRequested = Boolean(location.state?.pay)
  useEffect(() => {
    if (!fresh || !payRequested) return
    navigate(location.pathname, { replace: true, state: null })
    if (status?.provider === "asaas" && (status?.status === "trialing" || status?.pending)) pay(false)
    else openErrorSnackbar("Não há nenhum Pix para pagar agora.")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh, payRequested])

  const backToOverview = useCallback(() => {
    setView({ name: "overview" })
    setDialog(null)
    refresh()
  }, [refresh])

  const goBack = () =>
    view.name === "overview" && !dialog ? navigate("/profile") : backToOverview()

  return (
    <div className="planScreen__wrapper">
      <CosmosBackground />
      <div className="planScreen subscriptionScreen pixCheckout">
        <img className="planScreen__logo" src={process.env.PUBLIC_URL + "/Primário_5.svg"} alt="Cosmo" />
        <button className="planScreen__back" onClick={goBack}>
          <ArrowBackIcon style={{ fontSize: "1rem" }} /> Voltar
        </button>

        {view.name === "overview" &&
          (fetched && status ? (
            <Overview
              status={status}
              pending={pending}
              migration={migration}
              switchWindow={switchWindow}
              reauthorize={reauthorize}
              plan={plan}
              scheduledPlan={scheduledPlan}
              planChangeAvailable={planChangeAvailable}
              trial={trial}
              periodEnd={periodEnd}
              busy={busy}
              dialog={dialog}
              onAskCancel={() => setDialog({ kind: "cancel" })}
              onConfirmCancel={confirmCancel}
              onAskPlanChange={askPlanChange}
              onConfirmPlanChange={confirmPlanChange}
              onUndoPlanChange={undoPlanChange}
              onCloseDialog={() => setDialog(null)}
              onReactivate={reactivate}
              onPortal={openPortal}
              onSwitch={startSwitch}
              onPay={() => pay(false)}
              onReauthorize={() => pay(true)}
              onPendingStripe={() => run("pending", () => openPendingPayment(pending, navigate))}
            />
          ) : (
            <div>
              <Skeleton variant="text" width="60%" height={40} />
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height={56} animation="wave" sx={{ borderRadius: "8px" }} />
              ))}
            </div>
          ))}

        {view.name === "plans" && (
          <>
            <h1>{view.to === "pix" ? "Trocar para Pix" : "Trocar para cartão"}</h1>
            <h2 className="planScreen__subtitle">
              {view.to === "pix"
                ? `Você paga agora o próximo período, que começa em ${periodEnd}.`
                : `O primeiro débito no cartão será em ${firstCardCharge(status?.current_period_end)}.`}
            </h2>
            {(plans ?? []).map((plan) => (
              <div
                key={plan.priceID}
                onClick={() => setSelectedPlan(plan)}
                className={`planScreen__plan${selectedPlan?.priceID === plan.priceID ? " planScreen__plan--selected" : ""}`}
              >
                <div className="planScreen__plan__body">
                  <h2>{plan.name}</h2>
                  {plan.description && <p>{plan.description}</p>}
                </div>
                <div className="planScreen__plan__right">
                  <div className="planScreen__plan__price">
                    <h3>{formatPrice(plan.amount)}</h3>
                    <p>{formatInterval(plan.recurrentInterval)}</p>
                  </div>
                  {selectedPlan?.priceID === plan.priceID && <CheckCircleIcon className="planScreen__plan__check" />}
                </div>
              </div>
            ))}
            <Button
              text="Continuar"
              onClick={view.to === "pix" ? goToAddress : switchToCard}
              isLoading={busy === "plans"}
              disabled={!selectedPlan || busy === "plans"}
            />
          </>
        )}

        {view.name === "address" && (
          <>
            <h1>Endereço de cobrança</h1>
            <h2 className="planScreen__subtitle">
              Precisamos do seu endereço para emitir a nota fiscal da assinatura.
            </h2>
            <AddressForm
              initialAddress={initialAddress ?? EMPTY_ADDRESS}
              onSubmit={(address) => switchToPix(view.priceID, address)}
              isLoading={busy === "address"}
              submitText="Gerar código Pix"
              invalidCity={invalidCity}
            />
          </>
        )}

        {view.name === "qr" && (
          <>
            <h1>Escaneie ou copie o código</h1>
            <h2 className="planScreen__subtitle">A confirmação aparece aqui assim que o pagamento for identificado.</h2>
            <PixQrCode
              key={view.key}
              qr={view.qr}
              paymentMethod={view.paymentMethod}
              onRegenerate={view.regenerate}
              regenerating={busy !== null}
              onPaid={backToOverview}
              isPaid={view.isPaid}
              paidMessage={view.paidMessage}
              expiredMessage="Gere um novo código para concluir o pagamento."
            />
          </>
        )}
      </div>
    </div>
  )
}

// The card trial ends when the Pix period does, but never in less than 48 h
// (api D14).
const firstCardCharge = (periodEndIso) => {
  const end = Date.parse(periodEndIso ?? "")
  const min = Date.now() + MIN_CARD_TRIAL_MS
  return formatInstantDate(new Date(Number.isNaN(end) ? min : Math.max(end, min)).toISOString())
}


// Notice of a running trial (copy.md T11): when it ends, the first charge,
// and for Pix "Pagar agora" (paying early keeps the trial days, api D82).
const trialNotice = (trial, isPix, onPay) => {
  const amount = typeof trial.amount === "number" ? ` de ${formatPrice(trial.amount)}` : ""
  const first = formatDate(trial.firstChargeDate ?? trial.endsAt)
  return {
    title: `Período de teste até ${formatDate(trial.endsAt)}`,
    body: isPix
      ? `A 1ª cobrança${amount} vence em ${first}. Cancele até lá e nada é cobrado.`
      : `A 1ª cobrança${amount} no cartão será em ${first}. Cancele até lá e nada é cobrado.`,
    buttons: isPix ? [{ text: "Pagar agora", onClick: onPay, key: "pay" }] : [],
  }
}

// Row of the subscription list: label, value, an optional hint and action.
function Row({ label, value, hint, action }) {
  return (
    <div className="subscriptionScreen__row">
      <span className="subscriptionScreen__rowBody">
        <span className="subscriptionScreen__rowLabel">{label}</span>
        <span className="subscriptionScreen__rowValue">{value}</span>
        {hint && <span className="subscriptionScreen__rowHint">{hint}</span>}
      </span>
      {action && (
        <button
          type="button"
          className="subscriptionScreen__rowAction"
          onClick={action.onClick}
          disabled={action.busy}
        >
          {action.busy ? "…" : action.label}
        </button>
      )}
    </div>
  )
}

const planDialogCopy = (dialog, plan, periodEnd) => {
  const targetLabel = PLAN_LABELS[dialog.target.recurrentInterval]
  const currentLabel = PLAN_LABELS[plan?.interval] ?? ""
  const toAnnual = dialog.target.recurrentInterval === "year"
  const savings = toAnnual && dialog.monthlyAmount ? dialog.monthlyAmount * 12 - dialog.target.amount : 0
  const price = `${formatPrice(dialog.target.amount)} ${toAnnual ? "por ano" : "por mês"}`
  return {
    title: `Mudar para o plano ${targetLabel}?`,
    body:
      `Você continua no ${currentLabel} até ${periodEnd}. A partir daí, paga ${price}` +
      (savings > 0 ? ` e economiza ${formatPrice(savings)}` : "") +
      ". Dá para desfazer até lá.",
    confirm: `Mudar para ${targetLabel}`,
  }
}

/**
 * Overview of Minha assinatura (D68, prototype B): a notice only when there
 * is something to do (pending, past due, ending, boleto, a running trial —
 * T11), then the list —
 * plan (change at the next renewal, D69), next charge, payment method
 * (switch, D74), Pix Automático (D44, D77), card and invoices — and
 * "Cancelar assinatura" with an inline confirmation whose primary action
 * keeps the subscription.
 */
export function Overview({
  status,
  pending,
  migration,
  switchWindow,
  reauthorize,
  plan,
  scheduledPlan,
  planChangeAvailable,
  trial,
  periodEnd,
  busy,
  dialog,
  onAskCancel,
  onConfirmCancel,
  onAskPlanChange,
  onConfirmPlanChange,
  onUndoPlanChange,
  onCloseDialog,
  onReactivate,
  onPortal,
  onSwitch,
  onPay,
  onReauthorize,
  onPendingStripe,
}) {
  const isPix = status.provider === "asaas"
  const isStripe = status.provider === "stripe"
  const isCard = isStripe && status.payment_method === "card"
  // Stripe "unpaid" (retries exhausted, subscription kept) settles like past_due.
  const pastDue = UNPAID_STATUSES.includes(status.status)
  const ending = status.cancel_at_period_end
  const settled = !pastDue && !ending && !migration

  // Promotional trial (api D92): running while trialing; a Pix trial that
  // ended unpaid reads as past due with the trial copy (T13).
  const inTrial = trial?.state === "running"

  const badge = pastDue
    ? { text: "Pagamento pendente", mod: "warning" }
    : migration
    ? { text: `Ativa até ${formatDayMonth(migration.deadline)}`, mod: "warning" }
    : ending
    ? { text: "Encerrando", mod: "warning" }
    : inTrial
    ? { text: "Em teste", mod: "active" }
    : { text: "Ativa", mod: "active" }

  // Notice: what needs the member now, with its one call to action.
  let notice = null
  if (pastDue) {
    const settling = pending ?? { kind: isPix ? "pix" : "card", graceDaysRemaining: status.grace_days_remaining ?? null }
    const cta = isPix
      ? { text: "Pagar com Pix", onClick: onPay, key: "pay" }
      : pending
      ? { text: pendingActionLabel(pending.kind), onClick: onPendingStripe, key: "pending" }
      : { text: "Atualizar cartão", onClick: onPortal, key: "portal" }
    notice = { ...pendingNotice(settling, trial), alert: true, buttons: [cta] }
  } else if (migration) {
    notice = {
      ...migrationNotice(migration.deadline),
      alert: true,
      buttons: [
        { text: "Continuar com Pix", onClick: () => onSwitch("pix"), key: "switch-pix", disabled: !switchWindow?.toPixAvailable },
        { text: "Continuar com cartão", onClick: () => onSwitch("card"), key: "switch-card", secondary: true },
      ],
    }
  } else if (ending) {
    notice = {
      title: `Sua assinatura termina em ${periodEnd}`,
      body: "Você continua com acesso até lá e não haverá novas cobranças. Mudou de ideia? É só continuar.",
      buttons: [{ text: "Continuar assinando", onClick: onReactivate, key: "reactivate" }],
    }
  } else if (inTrial) {
    notice = trialNotice(trial, isPix, onPay)
  } else if (isPix && pending) {
    notice = { ...pendingNotice(pending), buttons: [{ text: "Pagar com Pix", onClick: onPay, key: "pay" }] }
  }

  // Plan: change at the next renewal, or the scheduled one with its undo.
  const targetLabel = PLAN_LABELS[otherInterval(plan?.interval)]
  const planRow = {
    label: "Plano",
    value: planValue(plan),
    hint: scheduledPlan
      ? `Muda para o plano ${PLAN_LABELS[scheduledPlan.interval]} em ${formatDate(scheduledPlan.effectiveAt)}`
      : null,
    action:
      scheduledPlan && !pending && !pastDue
        ? { label: "Desfazer", onClick: onUndoPlanChange, busy: busy === "undoPlan" }
        : !scheduledPlan && planChangeAvailable && settled
        ? { label: `Mudar para ${targetLabel?.toLowerCase()}`, onClick: onAskPlanChange, busy: busy === "plan" }
        : null,
  }

  const dateRow = pastDue
    ? pending?.dueDate && { label: "Venceu em", value: formatDate(pending.dueDate) }
    : ending || migration
    ? periodEnd && { label: "Acesso até", value: periodEnd }
    : status.next_charge_date && {
        label:
          status.payment_method === "pix_automatic"
            ? "Próximo débito"
            : isPix
            ? "Próxima cobrança"
            : "Próxima renovação",
        value: formatDate(status.next_charge_date),
      }

  const methodRow = { label: "Forma de pagamento", value: METHOD_LABELS[status.payment_method] ?? "—" }
  if (settled && isPix) {
    methodRow.action = { label: "Trocar", onClick: () => onSwitch("card"), busy: busy === "switch-card" }
  } else if (settled && isStripe && switchWindow) {
    // Card, or boleto before the migration scheduled its end (D13).
    if (switchWindow.toPixAvailable) {
      methodRow.action = { label: "Trocar", onClick: () => onSwitch("pix"), busy: busy === "switch-pix" }
    } else if (switchWindow.windowOpensAt) {
      methodRow.hint = `Troca para Pix disponível a partir de ${formatDate(switchWindow.windowOpensAt)}`
    }
  }

  // Pix Automático: on, or offered to a monthly-Pix member (D44, D48, D77).
  let autoDebitRow = null
  if (settled && status.payment_method === "pix_automatic") {
    autoDebitRow = { label: "Débito automático", value: "Ligado" }
  } else if (settled && status.payment_method === "pix" && reauthorize) {
    autoDebitRow = { label: "Débito automático", value: "Desligado" }
    if (scheduledPlan) {
      autoDebitRow.hint = "Para ativar, desfaça a mudança de plano."
    } else if (reauthorize.available) {
      autoDebitRow.action = { label: "Ativar", onClick: onReauthorize, busy: busy === "reauthorize" }
    } else if (reauthorize.windowOpensAt) {
      autoDebitRow.hint = `Disponível a partir de ${formatDate(reauthorize.windowOpensAt)}`
    }
  }

  const cardRow = isCard && !migration
    ? {
        label: "Cartão e faturas",
        value: "Atualize o cartão e veja suas faturas",
        action: { label: "Abrir", onClick: onPortal, busy: busy === "portal" },
      }
    : null

  const rows = [planRow, dateRow, methodRow, autoDebitRow, cardRow].filter(Boolean)

  const dialogCopy =
    dialog?.kind === "cancel" && inTrial
      ? {
          // copy.md T12: nothing is charged when a trial is cancelled (D86).
          title: "Cancelar o teste?",
          body: `Você mantém o acesso até ${formatDate(trial.endsAt)} e nada será cobrado. Se mudar de ideia, dá para continuar até lá.`,
          primary: { text: "Manter minha assinatura", onClick: onCloseDialog },
          secondary: { text: "Cancelar mesmo assim", onClick: onConfirmCancel, busy: busy === "cancel", danger: true },
        }
      : dialog?.kind === "cancel"
      ? {
          title: "Cancelar a assinatura?",
          body: `Você mantém o acesso até ${periodEnd} e não haverá novas cobranças. Se mudar de ideia, dá para desfazer até lá.`,
          primary: { text: "Manter minha assinatura", onClick: onCloseDialog },
          secondary: { text: "Cancelar mesmo assim", onClick: onConfirmCancel, busy: busy === "cancel", danger: true },
        }
      : dialog?.kind === "plan"
      ? (() => {
          const copy = planDialogCopy(dialog, plan, periodEnd)
          return {
            title: copy.title,
            body: copy.body,
            primary: { text: copy.confirm, onClick: onConfirmPlanChange, busy: busy === "plan" },
            secondary: { text: "Agora não", onClick: onCloseDialog },
          }
        })()
      : null

  return (
    <>
      <div className="subscriptionScreen__header">
        <h1>Minha assinatura</h1>
        <span className={`subscriptionScreen__badge subscriptionScreen__badge--${badge.mod}`}>{badge.text}</span>
      </div>

      {notice && (
        <div className={`subscriptionScreen__notice${notice.alert ? " subscriptionScreen__notice--alert" : ""}`}>
          <p className="subscriptionScreen__noticeTitle">{notice.title}</p>
          <p>{notice.body}</p>
          {notice.buttons.map((button) => (
            <Button
              key={button.key}
              text={button.text}
              onClick={button.onClick}
              isLoading={busy === button.key}
              disabled={button.disabled || busy === button.key}
              className={button.secondary ? "subscriptionScreen__secondary" : ""}
            />
          ))}
        </div>
      )}

      {dialogCopy ? (
        <div className="subscriptionScreen__dialog" role="group" aria-label={dialogCopy.title}>
          <p className="subscriptionScreen__noticeTitle">{dialogCopy.title}</p>
          <p>{dialogCopy.body}</p>
          <Button
            text={dialogCopy.primary.text}
            onClick={dialogCopy.primary.onClick}
            isLoading={dialogCopy.primary.busy}
            disabled={dialogCopy.primary.busy}
          />
          <button
            type="button"
            className={`subscriptionScreen__link${dialogCopy.secondary.danger ? " subscriptionScreen__link--danger" : ""}`}
            onClick={dialogCopy.secondary.onClick}
            disabled={dialogCopy.secondary.busy}
          >
            {dialogCopy.secondary.busy ? "Cancelando…" : dialogCopy.secondary.text}
          </button>
        </div>
      ) : (
        <>
          <div className="subscriptionScreen__card">
            {rows.map((row) => (
              <Row key={row.label} {...row} />
            ))}
          </div>
          {settled && (status.status === "active" || status.status === "trialing") && (
            <button type="button" className="subscriptionScreen__link" onClick={onAskCancel}>
              Cancelar assinatura
            </button>
          )}
        </>
      )}
    </>
  )
}
